import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { Phone, Mail, MapPin, ArrowRight, Calendar } from 'lucide-react';
import { BUSINESS } from '../../data/businessInfo';

// Direkte, zuverlässige Head-Manipulation für SEO-Tags.
// Ersetzt vorhandene Tags aus index.html und räumt beim Unmount wieder auf.
function useSeoHead({ title, description, canonical }) {
  useEffect(() => {
    // Backup der aktuellen (statischen) Werte für Cleanup
    const prevTitle = document.title;
    const set = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith('meta') ? 'meta' : 'link');
        const match = selector.match(/\[(\w+)="([^"]+)"\]/);
        if (match) el.setAttribute(match[1], match[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    document.title = title;
    set('meta[name="description"]', 'content', description);
    set('meta[name="title"]', 'content', title);
    set('link[rel="canonical"]', 'href', canonical);
    set('meta[property="og:url"]', 'content', canonical);
    set('meta[property="og:title"]', 'content', title);
    set('meta[property="og:description"]', 'content', description);
    set('meta[property="og:type"]', 'content', 'article');
    set('meta[property="twitter:url"]', 'content', canonical);
    set('meta[property="twitter:title"]', 'content', title);
    set('meta[property="twitter:description"]', 'content', description);

    return () => {
      // Beim Verlassen der Seite Title zurücksetzen (Home-Titel bleibt intakt)
      document.title = prevTitle;
    };
  }, [title, description, canonical]);
}

// Wiederverwendbares Layout für alle neuen SEO-Unterseiten
// KOMPLETT UNABHÄNGIG von der Home-Startseite — überschreibt nichts.
export function SeoPage({
  title,
  description,
  canonicalPath,
  jsonLd,
  kicker,
  heroTitle,
  heroSubtitle,
  children,
  showCta = true
}) {
  const canonical = `${BUSINESS.domain}${canonicalPath}`;
  useSeoHead({ title, description, canonical });
  const schemas = Array.isArray(jsonLd) ? jsonLd : [];
  return (
    <div className="min-h-screen bg-white text-[#1A1A1A]">
      <Helmet>
        {schemas.map((schema, i) => (
          <script key={`ld-${i}`} type="application/ld+json">{JSON.stringify(schema)}</script>
        ))}
      </Helmet>

        {/* Sub-Nav für SEO-Bereich, NICHT die Hauptnavigation */}
        <div className="border-b border-gray-100 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-[#9B1B30] transition-colors">← Startseite</Link>
            <Link to="/maler-dormagen" className="hover:text-[#9B1B30]">Maler Dormagen</Link>
            <Link to="/leistungen" className="hover:text-[#9B1B30]">Leistungen</Link>
            <Link to="/einsatzgebiet" className="hover:text-[#9B1B30]">Einsatzgebiet</Link>
            <Link to="/so-arbeiten-wir" className="hover:text-[#9B1B30]">Ablauf</Link>
            <Link to="/ratgeber" className="hover:text-[#9B1B30]">Ratgeber</Link>
            <Link to="/faq" className="hover:text-[#9B1B30]">FAQ</Link>
            <Link to="/kontakt" className="hover:text-[#9B1B30]">Kontakt</Link>
          </div>
        </div>

      {/* Hero */}
      <header className="bg-gradient-to-b from-[#faf7f5] to-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          {kicker && (
            <div className="inline-block px-3 py-1 rounded-full bg-[#9B1B30] text-white text-xs font-semibold tracking-wide uppercase mb-4">
              {kicker}
            </div>
          )}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-[#1A1A1A] mb-4">
            {heroTitle}
          </h1>
          {heroSubtitle && (
            <p className="text-lg text-gray-600 max-w-3xl leading-relaxed">
              {heroSubtitle}
            </p>
          )}
          <div className="flex flex-wrap gap-3 mt-6">
            <a
              href={`tel:${BUSINESS.phone}`}
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#9B1B30] hover:bg-[#7a1526] text-white rounded-lg font-semibold transition-colors"
            >
              <Phone className="w-4 h-4" /> {BUSINESS.phoneDisplay}
            </a>
            <Link
              to="/termin"
              className="inline-flex items-center gap-2 px-5 py-3 border-2 border-[#9B1B30] text-[#9B1B30] hover:bg-[#9B1B30] hover:text-white rounded-lg font-semibold transition-colors"
            >
              <Calendar className="w-4 h-4" /> Termin buchen
            </Link>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14 prose prose-lg max-w-none">
        {children}
      </main>

      {/* CTA + Kontakt-Footer für SEO-Seiten */}
      {showCta && (
        <section className="bg-[#1A1A1A] text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">
              Interesse an einem konkreten Angebot?
            </h2>
            <p className="text-gray-300 mb-6 max-w-2xl">
              Rufen Sie uns an oder buchen Sie direkt einen Besichtigungstermin. Wir melden uns
              werktags in der Regel am selben Tag zurück.
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              <a href={`tel:${BUSINESS.phone}`} className="flex items-center gap-3 p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                <Phone className="w-5 h-5 text-[#9B1B30]" />
                <div>
                  <div className="text-xs text-gray-400 uppercase">Telefon</div>
                  <div className="font-semibold">{BUSINESS.phoneDisplay}</div>
                </div>
              </a>
              <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-3 p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                <Mail className="w-5 h-5 text-[#9B1B30]" />
                <div>
                  <div className="text-xs text-gray-400 uppercase">E-Mail</div>
                  <div className="font-semibold text-sm">{BUSINESS.email}</div>
                </div>
              </a>
              <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg">
                <MapPin className="w-5 h-5 text-[#9B1B30]" />
                <div>
                  <div className="text-xs text-gray-400 uppercase">Adresse</div>
                  <div className="font-semibold text-sm">
                    {BUSINESS.street}, {BUSINESS.postalCode} {BUSINESS.city}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-white/10 text-sm text-gray-400">
              Mehr über {BUSINESS.name} auf der Unternehmenswebsite:{' '}
              <a href={BUSINESS.externalSite} target="_blank" rel="noopener" className="text-white hover:text-[#9B1B30] underline">
                farbwelt-service.de
              </a>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export function CtaInline({ text = 'Kostenloses Angebot anfordern' }) {
  return (
    <div className="not-prose my-8 p-6 bg-[#faf7f5] border-l-4 border-[#9B1B30] rounded-r-lg">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="font-semibold text-lg text-[#1A1A1A]">{text}</div>
          <div className="text-sm text-gray-600">Persönliche Beratung, transparente Preise, ohne Aufpreis am Wochenende</div>
        </div>
        <Link to="/termin" className="inline-flex items-center gap-2 px-5 py-3 bg-[#9B1B30] hover:bg-[#7a1526] text-white rounded-lg font-semibold whitespace-nowrap">
          Termin buchen <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
